import React, { useEffect, useMemo } from 'react'
import { Button, Form, Input, Modal, Select, Tag } from 'antd'
import type { EditUserPayload, Member, Organization, OrgRole } from '../../types'
import { HARD_MONEY_ROLE_OPTIONS } from '../../constants'
import { managerCanAssignOwner, managerHasSuperAdmin } from '../../utils'

interface EditUserModalProps {
  open: boolean
  member: Member | null
  organization: Organization | null
  submitting?: boolean
  onCancel: () => void
  onSubmit: (payload: EditUserPayload) => Promise<void>
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  open,
  member,
  organization,
  submitting = false,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<{
    firstName: string
    lastName: string
    email: string
    nmls: string
    roles: OrgRole[]
  }>()

  const role = organization && member ? member.roles[organization.id] : undefined
  const currentRoles = useMemo(
    () => (organization && member
      ? member.roleLists?.[organization.id] || (role ? [role] : [])
      : []),
    [member, organization, role],
  )
  const protectedRole = currentRoles.some((item) => item === 'Owner' || item === 'Super Admin')
  const canManageSuperAdminRole = managerHasSuperAdmin(organization)
  const canManageOwnerRole = managerCanAssignOwner(organization)
  const standardRoleOptions: OrgRole[] = ['Loan Officer', 'Processor']
  const baseRoleOptions: OrgRole[] = organization?.isHardMoneyLoanCustomer
    ? [...standardRoleOptions, ...HARD_MONEY_ROLE_OPTIONS]
    : [...standardRoleOptions]
  const availableRoleOptions: OrgRole[] = canManageSuperAdminRole
    ? [...baseRoleOptions, 'Owner', 'Super Admin']
    : canManageOwnerRole
      ? [...baseRoleOptions, 'Owner']
      : baseRoleOptions
  const roleOptions = currentRoles.reduce<OrgRole[]>((options, currentRole) => (
    options.includes(currentRole) ? options : [...options, currentRole]
  ), availableRoleOptions)

  useEffect(() => {
    if (!open || !member || !organization) return
    form.setFieldsValue({
      firstName: member.firstName,
      lastName: member.lastName,
      email: member.email,
      nmls: member.nmls === '-' ? '' : member.nmls,
      roles: currentRoles.length > 0 ? currentRoles : ['Loan Officer'],
    })
  }, [currentRoles, form, member, open, organization])

  const handleSubmit = async () => {
    if (!member || !organization) return
    const values = await form.validateFields()
    const selectedRoles: OrgRole[] = values.roles?.length ? values.roles : ['Loan Officer']
    const nextRoles = canManageSuperAdminRole
      ? selectedRoles
      : selectedRoles.filter((item) => item !== 'Super Admin').concat(
        currentRoles.includes('Super Admin') ? ['Super Admin' as OrgRole] : [],
      )
    await onSubmit({
      customerId: organization.customerId,
      id: member.id,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: protectedRole ? member.email : values.email.trim().toLowerCase(),
      nmls: values.nmls?.trim() || '',
      roles: nextRoles.length > 0 ? nextRoles : ['Loan Officer'],
      accountTier: '',
    })
  }

  return (
    <Modal
      open={open}
      width={620}
      footer={null}
      destroyOnClose
      onCancel={onCancel}
      className="um-refactor__modal"
      title="Edit user's information"
    >
      {organization && (
        <div className="um-refactor__edit-context">
          <span>{organization.name}</span>
          {role && <Tag className="um-refactor__plan-badge">{role}</Tag>}
        </div>
      )}

      <Form form={form} layout="vertical" requiredMark={false}>
        <div className="um-refactor__field-row">
          <Form.Item name="firstName" label="First name" className="um-refactor__form-item" rules={[{ required: true, message: 'First name is required' }]}>
            <Input size="large" placeholder="Enter first name" />
          </Form.Item>
          <Form.Item name="lastName" label="Last name" className="um-refactor__form-item" rules={[{ required: true, message: 'Last name is required' }]}>
            <Input size="large" placeholder="Enter last name" />
          </Form.Item>
        </div>

        {!protectedRole && (
          <Form.Item name="email" label="Email address" rules={[{ required: true, type: 'email', message: 'Enter a valid email address' }]}>
            <Input size="large" placeholder="Enter loan officer email address" />
          </Form.Item>
        )}

        <Form.Item
          name="roles"
          label="Role"
          extra="The user needs to sign in again for the new role to take effect."
          rules={[{ required: true, message: 'Select at least one role' }]}
          normalize={(roles: OrgRole[] | undefined) => {
            const locked = currentRoles.filter((item) =>
              item === 'LO Manager' || item === 'Owner' || item === 'Super Admin',
            )
            const next = [...new Set([...locked, ...(roles || [])])]
            return next.length > 0 ? next : ['Loan Officer']
          }}
        >
          <Select
            size="large"
            mode="multiple"
            options={roleOptions.map((option) => ({
              label: option === 'Owner' ? 'Account owner' : option === 'Processor' ? 'Loan processor' : option,
              value: option,
              disabled: (option === 'LO Manager' && currentRoles.includes('LO Manager')) ||
                (option === 'Owner' && currentRoles.includes('Owner')) ||
                (option === 'Super Admin' && (currentRoles.includes('Super Admin') || !canManageSuperAdminRole)),
            }))}
          />
        </Form.Item>

        <Form.Item
          name="nmls"
          label={
            <span>
              NMLS number <span className="um-refactor__label-hint">(Required for all roles other than processors)</span>
            </span>
          }
        >
          <Input size="large" placeholder="Enter loan officer NMLS number" />
        </Form.Item>

        <Button type="primary" block size="large" loading={submitting} className="um-refactor__btn-primary--full" onClick={handleSubmit}>
          Confirm
        </Button>
      </Form>
    </Modal>
  )
}

export default EditUserModal
